<!-- eslint-disable vue/no-mutating-props -->
<template>
  <div class="emission-step">
    <h2 class="step-title">Datos del Vehículo</h2>
    <p class="step-subtitle">Completa los datos adicionales de tu vehículo</p>

    <div class="form-section">
      <div class="section-header">
        <el-icon class="section-icon"><Box /></el-icon>
        <span class="section-title">DATOS ADICIONALES DEL VEHÍCULO</span>
      </div>
      <div class="form-grid">
        <div class="form-field">
          <label class="field-label">Número de serie (VIN) <span class="required">*</span></label>
          <el-input
            v-model="formData.numeroSerie"
            placeholder="Ingresa el VIN de 17 caracteres alfanuméricos"
            class="field-input"
            maxlength="17"
          />
          <span class="field-hint">
            El VIN se encuentra en el tablero del lado del conductor (a la altura del parabrisas), en el marco de la
            puerta del conductor, o en el manual del propietario, título o documentos del seguro.
          </span>
        </div>
        <div class="form-field">
          <label class="field-label">Número de motor</label>
          <el-input v-model="formData.numeroMotor" placeholder="Ingresa el número de motor" class="field-input" />
          <span class="field-hint">
            Número de serie o código consecutivo asignado durante la producción para identificar específicamente el motor.
            Su longitud y formato dependen de la marca y el modelo del vehículo.
          </span>
        </div>
        <div class="form-field">
          <label class="field-label">Placas</label>
          <el-input v-model="formData.placas" placeholder="Ingresa las placas del vehículo" class="field-input" maxlength="10" />
        </div>
      </div>
    </div>

    <div class="privacy-section">
      <el-checkbox v-model="formData.aceptaPolitica" class="privacy-checkbox">
        He leído y acepto la política de privacidad
      </el-checkbox>
      <span v-if="!formData.aceptaPolitica && showPrivacyError" class="error-text">Campo requerido</span>
    </div>

    <div v-if="emissionSuccess" class="form-section success-section">
      <div class="success-message">
        <el-icon class="success-icon"><Check /></el-icon>
        <div class="success-content">
          <h3 class="success-title">
            {{ allowEmission ? '¡Póliza emitida exitosamente!' : '¡Cotización actualizada exitosamente!' }}
          </h3>
          <p v-if="allowEmission && polizaEmitida?.Poliza_Numero" class="success-subtitle">
            Número de póliza: <strong>{{ polizaEmitida.Poliza_Numero }}</strong>
          </p>
          <p v-else-if="!allowEmission" class="success-subtitle">Tu cotización ha sido actualizada con los datos proporcionados.</p>
        </div>
      </div>
    </div>

    <div v-if="emissionSuccess && documentosDisponibles.length > 0" class="form-section">
      <div class="section-header">
        <el-icon class="section-icon"><Download /></el-icon>
        <span class="section-title">DOCUMENTOS DISPONIBLES</span>
      </div>
      <div class="documentos-grid">
        <div v-for="(documento, index) in documentosDisponibles" :key="index" class="documento-item">
          <el-button type="primary" size="default" @click="emit('download-document', documento.Guid_Documento, documento.Nombre)" class="download-button">
            <el-icon><Download /></el-icon>
            {{ documento.Nombre || `Descargar ${documento.Tipo || 'Documento'}` }}
          </el-button>
        </div>
      </div>
    </div>

    <div v-if="emissionSuccess && documentosDisponibles.length === 0 && !isLoadingDocumentos" class="form-section">
      <div class="info-message">
        <p>Los documentos estarán disponibles próximamente.</p>
      </div>
    </div>

    <div class="step-actions">
      <el-button type="default" size="large" @click="emit('back')" class="back-button">
        ← Atrás
      </el-button>
      <div class="action-buttons">
        <el-button
          v-if="!emissionSuccess"
          type="primary"
          size="large"
          :disabled="!canProceed"
          class="action-button emit-button"
          @click="emit('next')"
        >
          {{ allowEmission ? 'CONTINUAR' : 'FINALIZAR COTIZACION' }}
        </el-button>
        <el-button v-if="emissionSuccess" type="default" size="large" @click="emit('close')" class="action-button">
          CERRAR
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Box, Check, Download } from '@element-plus/icons-vue';
import type { Documento, EmissionFormData, PolizaEmitida } from '@/types/emission.types';

interface Props {
  formData: EmissionFormData;
  canProceed: boolean;
  showPrivacyError: boolean;
  emissionSuccess: boolean;
  allowEmission: boolean;
  polizaEmitida: PolizaEmitida | null;
  documentosDisponibles: Documento[];
  isLoadingDocumentos: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  back: [];
  next: [];
  close: [];
  'download-document': [guidDocumento: string, nombre?: string];
}>();
</script>

